import React, { useState } from "react";
import { X, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";

/**
 * Email a Customer Statement PDF via Resend.
 *
 * Pre-fills the To field with the customer's email address (if any) and the
 * subject with a default like "Statement of account — {name} — {period}".
 */
export const StatementEmailModal = ({ customer, statement, dateFrom, dateTo, onClose }) => {
  const closing = Number(statement?.closing_balance || 0);
  const periodLabel = `${dateFrom || "opening"} → ${dateTo || "today"}`;

  const [to, setTo] = useState(customer?.email || "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(
    `Statement of account — ${customer?.name || "Customer"} — ${periodLabel}`,
  );
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const parseList = (s) =>
    s.split(/[,;\s]+/).map((x) => x.trim()).filter((x) => /@/.test(x));

  const send = async () => {
    const toList = parseList(to);
    if (toList.length === 0) {
      toast.error("Enter at least one recipient email");
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.sendStatementEmail(
        customer.id,
        {
          to: toList,
          cc: parseList(cc),
          subject: subject.trim() || null,
          message: message.trim() || null,
        },
        dateFrom || null,
        dateTo || null,
      );
      if (res.ok) {
        toast.success(
          `Statement emailed to ${toList.join(", ")}` +
            (res.used_sandbox ? " · from Resend sandbox" : ""),
        );
        onClose();
      } else {
        toast.error(res.error || res.hint || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="statement-email-modal"
    >
      <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">
              Email Statement
            </div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              Send statement to customer
            </h2>
            <p className="text-xs text-[#3f4b52] mt-1">
              {customer?.code} · {customer?.name} · Period {periodLabel} · Closing{" "}
              <b className={closing > 0 ? "text-[#dc2626]" : "text-[#10b981]"}>{money(closing)}</b>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#3f4b52]"
            aria-label="Close"
            data-testid="stmt-email-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              To (comma-separated)
            </label>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="accounts@customer.co.za"
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="stmt-email-to"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              CC (optional)
            </label>
            <input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="broker@example.com"
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="stmt-email-cc"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Subject
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="stmt-email-subject"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Message (optional — replaces default intro)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Add a personal note…"
              className="w-full mt-1 px-3 py-2 border border-[#cfd7db] rounded-md text-sm"
              data-testid="stmt-email-message"
            />
          </div>

          <div className="border border-[#cfd7db] rounded-md p-3 bg-[#f5f6f6] text-xs text-[#3f4b52] flex items-start gap-2">
            <Mail size={14} className="text-[#f28a16] shrink-0 mt-0.5" />
            <div>
              The statement PDF for <b>{periodLabel}</b> will be attached automatically. Bank
              details are printed on the PDF, so the customer can pay directly from the email.
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]"
            data-testid="stmt-email-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={busy}
            data-testid="stmt-email-send-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Sending…" : "Send statement"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatementEmailModal;
